import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Plus, Trash2, Pencil, Target, Gift } from "lucide-react";
import { toast } from "sonner";

const TRIGGERS = ["order.delivered", "product.created", "profile.updated", "offer.set", "review.received", "coupon.created"];
const METRIC_OPS = ["count_orders", "sum_revenue", "unique_customers", "products_count", "profile_completion", "has_field", "offer_active", "count_reviews", "coupons_count"];
const PERIODS = ["once", "daily", "weekly", "monthly"];
const CATEGORIES = ["Avvio", "Visibilità", "Vendite", "Qualità", "Promozione", "Community"];

const EMPTY = {
  code: "", category: "Vendite", title: "", description: "", icon: "target",
  trigger: "order.delivered", metric_op: "count_orders", period: "weekly",
  tiers: JSON.stringify([{ key: "bronze", label: "Bronzo", threshold: 10, reward: { credits: 100, xp: 100 } }], null, 2),
};

export default function CeoMissions() {
  const [missions, setMissions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [wm, setWm] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/ceo/missions"); setMissions(data.missions || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/ceo/welcome-mission").then(({ data }) => setWm(data)).catch(() => {}); }, []);

  const saveWm = async () => {
    try { const { data } = await api.put("/ceo/welcome-mission", wm); setWm(data); toast.success("Missione di benvenuto aggiornata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (m) => {
    try { await api.put(`/ceo/missions/${m.code}`, { active: !m.active }); load(); toast.success(m.active ? "Missione disattivata" : "Missione attivata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(false); setForm(EMPTY); setOpen(true); };
  const openEdit = (m) => {
    setEditing(true);
    setForm({
      code: m.code, category: m.category || "Vendite", title: m.title || "", description: m.description || "",
      icon: m.icon || "target", trigger: m.trigger || "order.delivered",
      metric_op: (m.metric || {}).op || "count_orders", period: m.period || "once",
      tiers: JSON.stringify(m.tiers || [], null, 2),
    });
    setOpen(true);
  };

  const save = async () => {
    let tiers;
    try { tiers = JSON.parse(form.tiers); } catch { toast.error("Tiers: JSON non valido"); return; }
    if (!form.code || !form.title) { toast.error("Codice e titolo obbligatori"); return; }
    const metric = { op: form.metric_op };
    if (form.metric_op === "sum_revenue") metric.field = "total";
    if (form.metric_op === "has_field") metric.field = "image";
    if (form.metric_op === "count_reviews") metric.min_rating = 4;
    const payload = { code: form.code.trim(), category: form.category, title: form.title, description: form.description,
      icon: form.icon, trigger: form.trigger, metric, period: form.period, tiers };
    try { await api.post("/ceo/missions", payload); setOpen(false); load(); toast.success("Missione salvata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (code) => {
    if (!window.confirm("Eliminare questa missione?")) return;
    try { await api.delete(`/ceo/missions/${code}`); load(); toast.success("Missione eliminata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="ceo-missions">
      {wm && (
        <div className="bg-[#1C1C1F] border border-[#00E676]/20 rounded-xl p-5" data-testid="welcome-mission-config">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <h3 className="font-semibold flex items-center gap-2"><Gift className="h-5 w-5 text-[#00E676]" /> Missione di Benvenuto</h3>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="wm-enabled" checked={!!wm.enabled} onCheckedChange={(v) => setWm({ ...wm, enabled: v })} /> Attiva</label>
          </div>
          <p className="text-sm text-zinc-400 mb-3">Sbloccata automaticamente alla registrazione del cliente. Ricompensa configurabile.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label>Crediti bonus</Label><Input type="number" data-testid="wm-credits" value={wm.credits} onChange={(e) => setWm({ ...wm, credits: parseInt(e.target.value) || 0 })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            <div><Label>XP</Label><Input type="number" data-testid="wm-xp" value={wm.xp} onChange={(e) => setWm({ ...wm, xp: parseInt(e.target.value) || 0 })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            <div><Label>Badge</Label><Input data-testid="wm-badge-label" value={wm.badge_label} onChange={(e) => setWm({ ...wm, badge_label: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          </div>
          <Button size="sm" data-testid="wm-save" onClick={saveWm} className="bg-[#00E676] text-black rounded-md mt-3">Salva missione benvenuto</Button>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><Target className="h-5 w-5 text-[#00E676]" /> Missioni Attività</h3>
          <p className="text-sm text-zinc-400">Crea e gestisci le missioni gamificate senza rilasciare codice. Modifiche attive entro 60s.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="ceo-new-mission" onClick={openNew} className="bg-[#00E676] text-black rounded-md"><Plus className="h-4 w-4 mr-1" /> Nuova missione</Button></DialogTrigger>
          <DialogContent className="bg-[#1C1C1F] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Modifica missione" : "Nuova missione"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Codice</Label><Input data-testid="mission-code" disabled={editing} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="es. sales_weekend" className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
                <div><Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="mission-category" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Titolo</Label><Input data-testid="mission-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
              <div><Label>Descrizione</Label><Textarea data-testid="mission-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Trigger</Label>
                  <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                    <SelectTrigger data-testid="mission-trigger" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Metrica</Label>
                  <Select value={form.metric_op} onValueChange={(v) => setForm({ ...form, metric_op: v })}>
                    <SelectTrigger data-testid="mission-metric" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{METRIC_OPS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Periodo</Label>
                  <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                    <SelectTrigger data-testid="mission-period" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{PERIODS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Tiers (JSON) — key, label, threshold, reward{`{credits,xp,badge?,map_boost?,commission_discount?}`}</Label>
                <Textarea data-testid="mission-tiers" value={form.tiers} onChange={(e) => setForm({ ...form, tiers: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1 font-mono text-xs h-40" />
              </div>
              <Button data-testid="mission-save" onClick={save} className="bg-[#00E676] text-black rounded-md w-full">Salva missione</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2">
        {missions.map((m) => (
          <div key={m.code} data-testid={`ceo-mission-${m.code}`} className="flex items-center justify-between bg-[#1C1C1F] border border-white/10 rounded-lg p-4 gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{m.title}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-400">{m.category}</span>
                <span className="text-[10px] text-zinc-500">{m.trigger} · {m.period} · {(m.metric || {}).op}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{(m.tiers || []).map((t) => `${t.label}:${t.threshold}`).join(" · ")}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="flex items-center gap-1.5">
                <Switch data-testid={`mission-toggle-${m.code}`} checked={!!m.active} onCheckedChange={() => toggle(m)} />
                <span className="text-xs text-zinc-400">{m.active ? "Attiva" : "Off"}</span>
              </div>
              <button data-testid={`mission-edit-${m.code}`} onClick={() => openEdit(m)} className="p-2 text-zinc-300 hover:bg-white/5 rounded"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`mission-del-${m.code}`} onClick={() => del(m.code)} className="p-2 text-red-400 hover:bg-white/5 rounded"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {missions.length === 0 && <div className="text-zinc-500 py-8 text-center">Nessuna missione. Creane una!</div>}
      </div>
    </div>
  );
}
